import { ReactNode, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { SatelliteGroupType, SelectedSatelliteType } from '../../types';
import './MultigroupSelect.scss';
import { GlobeSvg } from '../../components/header/Svgs';
import { DEFAULT_ORBIT_COLOR } from '../../../../utils/constants';
import CustomToolTip from '../../../CommonComponents/CustomToolTip';

interface Props {
    groups: SatelliteGroupType[];
    selected_group: SatelliteGroupType | null;
    satellites: SelectedSatelliteType[];
    selected_satellite: SelectedSatelliteType | null;
    set_selected_group: (value: SatelliteGroupType) => void;
    set_selected_satellite: (value: SelectedSatelliteType) => void;
    toggle_scenario__info: () => void;
    loading: boolean;
    infoTooltip?: string;
    infoIcon?: ReactNode;
}

const MultigroupSelect = ({
    groups,
    selected_group,
    satellites,
    selected_satellite,
    set_selected_group,
    set_selected_satellite,
    toggle_scenario__info,
    loading,
    infoTooltip = 'Orbit Details',
    infoIcon,
}: Props) => {

    useEffect(() => {
        const clickOutSide = (e: any) => {
            const dropdown1 = document.getElementById('dropdown__1');
            const dropdown2 = document.getElementById('dropdown__2');
            if (dropdown1 && !dropdown1.contains(e.target)) {
                dropdown1.classList.remove('active');
            }
            if (dropdown2 && !dropdown2.contains(e.target)) {
                dropdown2.classList.remove('active');
            }
        }
        document.addEventListener('click', clickOutSide);
        return () => {
            document.removeEventListener('click', clickOutSide);
        }
    }, [])

    const handleDropdownClick = (dropdownRef: string) => {
        const element = document.getElementById(dropdownRef);
        if (!element) return
        else if (!element.classList.contains('active')) {
            element.classList.add('active');
            if (dropdownRef === 'dropdown__1') {
                const secondElement = document.getElementById('dropdown__2');
                if (secondElement) secondElement.classList.remove('active');
            } else if (dropdownRef === 'dropdown__2') {
                const firstElement = document.getElementById('dropdown__1');
                if (firstElement) firstElement.classList.remove('active');
            }
        } else {
            element.classList.remove('active');
        }
    };

    const getGroupColor = (group: SatelliteGroupType | null) => {
        if (!group) return '';
        const color = (group?.value?.color !== '' && !group?.value?.color.includes('#')) ? JSON.parse(group?.value?.color) : DEFAULT_ORBIT_COLOR;
        return `rgba(${color})`;
    }


    return (
        <div className={`command__center__multigroup__selection__container ${loading && 'disabled'}`}>
            <div id='dropdown__1' className='command__center__group__select__container' onClick={() => {
                if (groups && groups?.length <= 1) return
                handleDropdownClick('dropdown__1')
            }}>
                <div className={`command__center__group__select__header ${(!groups || groups?.length <= 1) && 'hide__icon'}`}>
                    <div className='command__center__group__select__header__text__container'>
                        <div className='command__center__group__color__container' style={{ backgroundColor: getGroupColor(selected_group) }} />
                        <span className='command__center__group__select__header__text'>
                            <CustomToolTip title={selected_group?.label ?? 'Satellite Group'} placement='top-start'>
                                {selected_group?.label}
                            </CustomToolTip>
                        </span>
                    </div>
                    <div className='command__center__group__select__header__icon'>
                        <i className='fe fe-chevron-down' />
                    </div>
                </div>
                {groups && groups?.length > 1 &&
                    <div className='command__center__group__select__body'>
                        {groups &&
                            groups?.map(group => (
                                <div key={group.value.satelliteGroupId}
                                    className={`command__center__group__select__body__item ${group?.value?.satelliteGroupId === selected_group?.value.satelliteGroupId && 'active'}`}
                                    onClick={() => {
                                        set_selected_group(group);
                                    }}
                                >
                                    <div className='command__center__body__text__container'>
                                        <div className='command__center__group__color__container' style={{ backgroundColor: getGroupColor(group) }} />
                                        <span className={`command__center__body__text ${group?.value?.satelliteGroupId === selected_group?.value.satelliteGroupId && 'selected__text'}`}>
                                            <CustomToolTip title={group?.label ?? ''} placement='top-start'>{group?.label}</CustomToolTip>
                                        </span>
                                    </div>
                                </div>
                            ))}
                    </div>
                }
            </div>
            <div id='dropdown__2' className='command__center__group__select__container'
                onClick={() => {
                    if (satellites && satellites?.length <= 1) return
                    handleDropdownClick('dropdown__2')
                }}>
                <div className={`command__center__group__select__header ${(!satellites || satellites?.length <= 1) && 'hide__icon'}`}>
                    <div className='command__center__group__select__header__text__container'>
                        <span className='command__center__group__select__header__text'>
                            <CustomToolTip title={selected_satellite?.label ?? 'Satellite'} placement='top-start'>
                                {selected_satellite?.label}
                            </CustomToolTip>
                        </span>
                    </div>
                    <div className='command__center__group__select__header__icon'>
                        <i className='fe fe-chevron-down' />
                    </div>
                </div>
                {satellites && satellites?.length > 1 &&
                    <div className='command__center__group__select__body'>
                        {satellites?.map(satellite => (
                            <div key={satellite.value.satelliteId}
                                className={`command__center__group__select__body__item ${satellite?.value?.satelliteId === selected_satellite?.value.satelliteId && 'active'}`}
                                onClick={() => {
                                    set_selected_satellite(satellite);
                                }}
                            >
                                <div className='command__center__body__text__container'>
                                    <span className={`command__center__body__text ${satellite?.value?.satelliteId === selected_satellite?.value.satelliteId && 'selected__text'}`}>
                                        <CustomToolTip title={satellite?.label ?? ''} placement='top-start'>{satellite?.label}</CustomToolTip>
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                }
            </div>
            <button className='svg__container' onClick={(event) => {
                event.stopPropagation();
                toggle_scenario__info()
            }}>
                <CustomToolTip title={infoTooltip} placement='bottom'>
                    {loading ? <CircularProgress color='inherit' size={14} /> : (infoIcon ?? <GlobeSvg />)}
                </CustomToolTip>
            </button>
        </div>
    );
};

export default MultigroupSelect;
